import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { fetchSession } from '@/api/auth';
import type { Session } from '@/lib/auth-client';

interface UserContextType {
  session: Session | null;
  setSession: (session: Session | null) => void;
  verifyEmail: () => void;
  setOrganizationId: (id: string) => void;
  updateUser: (updates: Partial<Session['user']>) => void;
}

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [isPending, setIsPending] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const data = await fetchSession();
        setSession(data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsPending(false);
      }
    };

    init();
  }, []);

  if (isPending) {
    return null;
  }

  const verifyEmail = () => {
    setSession((prev) => {
      if (!prev) {
        return null;
      }

      return { ...prev, user: { ...prev.user, emailVerified: true } };
    });
  };

  const setOrganizationId = (id: string) => {
    setSession((prev) => {
      if (!prev) {
        return null;
      }

      return { ...prev, session: { ...prev.session, activeOrganizationId: id } };
    });
  };

  const updateUser = (updates: Partial<Session['user']>) => {
    setSession((prev) => {
      if (!prev) {
        return null;
      }

      return { ...prev, user: { ...prev.user, ...updates } };
    });
  };

  const value: UserContextType = {
    session,
    verifyEmail,
    setSession,
    setOrganizationId,
    updateUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
